import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

// Use the origin API endpoint
const API_URL = `${window.location.origin}/api`;

// Check if we're in a development environment
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check authentication status once on mount and redirect if needed
  useEffect(() => {
    // Clear any existing redirection flags on component mount
    sessionStorage.removeItem('isRedirecting');
    
    // Only redirect if the user is authenticated
    if (isAuthenticated && user) {
      const dashboardPath = 
        user.role === 'super_admin' ? '/admin-dashboard' : 
        user.role === 'agent_admin' ? '/agent-dashboard' : 
        '/enhanced-dashboard';
      
      // Navigate to the appropriate dashboard
      navigate(dashboardPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Auto-fill test credentials in development mode
  useEffect(() => {
    if (isDevelopment && isLogin) {
      setFormData({
        ...formData,
        email: 'user@test.com',
        password: 'password123'
      });
    }
  }, [isDevelopment, isLogin]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    setError('');
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError('');
    // Reset form data when toggling
    setFormData({
      email: isLogin ? '' : formData.email,
      password: '',
      name: '',
      confirmPassword: ''
    });
    
    // If switching back to login and in development mode, add test credentials
    if (!isLogin && isDevelopment) {
      setFormData({
        email: 'user@test.com',
        password: 'password123',
        name: '',
        confirmPassword: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      if (isLogin) {
        // Login flow
        const success = await login(formData.email, formData.password);
        
        if (success) {
          // Login was successful, manually navigate to the dashboard
          console.log('Login successful, navigating to dashboard...');
          
          // Get the user from localStorage in case the state hasn't updated yet
          let userToCheck = user;
          if (!userToCheck) {
            try {
              const storedUser = localStorage.getItem('user');
              if (storedUser) {
                userToCheck = JSON.parse(storedUser);
              }
            } catch (e) {
              console.error('Error parsing stored user:', e);
            }
          }
          
          // Determine which dashboard to redirect to
          let dashboardPath = '/enhanced-dashboard';
          if (userToCheck) {
            if (userToCheck.role === 'super_admin') {
              dashboardPath = '/admin-dashboard';
            } else if (userToCheck.role === 'agent_admin') {
              dashboardPath = '/agent-dashboard';
            }
          }
          
          // Force navigation
          console.log('Navigating to:', dashboardPath);
          navigate(dashboardPath, { replace: true });
        } else {
          setError('Invalid credentials. Please try again.');
        }
      } else {
        // Registration flow
        // Validate password match
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        // Validate password length
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters long');
          setLoading(false);
          return;
        }

        await register(formData.name, formData.email, formData.password);
        // After successful registration, redirect to dashboard
        navigate('/enhanced-dashboard');
      }
    } catch (err) {
      // More user-friendly error messages
      if (isLogin) {
        if (err.message.includes('timeout') || err.message.includes('ECONNABORTED')) {
          setError('Server connection timed out. Please try again later.');
        }
        else if (err.message.includes('Network Error') || err.message.includes('ERR_NAME_NOT_RESOLVED')) {
          setError('Connection error. The system cannot connect to the server right now. Please try again later.');
        }
        else if (err.message.includes('Invalid credentials')) {
          setError(err.message);
        }
        else {
          setError('Login failed. Please try again or contact support.');
        }
      } else {
        setError(
          err.response?.data?.message || 
          err.message || 
          'Registration failed. Please try again.'
        );
      }
      
      console.error(isLogin ? 'Login error:' : 'Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form-wrapper">
        <h2>{isLogin ? 'Login to Your Account' : 'Create an Account'}</h2>
        {isDevelopment && isLogin && (
          <div className="dev-mode-notice">
            <p>Development Mode</p>
            <p>Use: user@test.com / password123</p>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit} className="login-form">
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required={!isLogin}
                minLength={2}
                autoComplete="name"
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={isLogin ? 1 : 6}
              autoComplete={isLogin ? "current-password" : "new-password"}
            />
          </div>
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required={!isLogin}
                minLength={6}
                autoComplete="new-password"
              />
            </div>
          )}
          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? (isLogin ? 'Logging in...' : 'Registering...') : (isLogin ? 'Login' : 'Register')}
          </button>
          
          <div className="auth-toggle">
            <p>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button 
                type="button" 
                className="toggle-auth-button" 
                onClick={toggleAuthMode}
              >
                {isLogin ? 'Register here' : 'Login here'}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login; 